import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HeroCanvas } from '../HeroCanvas';
import { AppleIcon, GooglePlayIcon, ArrowIcon, BackerTiles } from '../Icons';
import { PhoneFrame } from '../PhoneMockups';
import { APP_STORE_URL, PLAY_STORE_URL, openStore } from '../appLinks';

export function Home() {
  const navigate = useNavigate();

  // Mouse-parallax tilt on the hero phone cluster — the phones lean toward the
  // cursor, turning the static mockups into a living centerpiece. Skipped when
  // the user prefers reduced motion.
  useEffect(() => {
    const hero = document.querySelector('.hero');
    const phones = document.querySelector('.hero-phones') as HTMLElement | null;
    if (!hero || !phones) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let raf = 0;
    const onMove = (e: Event) => {
      const ev = e as MouseEvent;
      const r = hero.getBoundingClientRect();
      const px = (ev.clientX - r.left) / r.width - 0.5;  // -0.5 .. 0.5
      const py = (ev.clientY - r.top) / r.height - 0.5;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        phones.style.setProperty('--ph-ry', `${px * 14}deg`);
        phones.style.setProperty('--ph-rx', `${-py * 9}deg`);
      });
    };
    const onLeave = () => {
      phones.style.setProperty('--ph-ry', '0deg');
      phones.style.setProperty('--ph-rx', '0deg');
    };

    hero.addEventListener('mousemove', onMove);
    hero.addEventListener('mouseleave', onLeave);
    return () => {
      hero.removeEventListener('mousemove', onMove);
      hero.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      {/* Hero */}
      <section className="hero">
        <HeroCanvas />
        <div className="hero-grain" />
        <div className="hero-overlay" />
        <div className="hero-inner">
          <div className="hero-text">
            <p className="hero-eyebrow">Professional music platform &nbsp;·&nbsp; India</p>
            <h1 className="hero-h1">Build your<br /><em>music career</em><br />in one place.</h1>
            <p className="hero-sub">From your profile to your first paid gig — Tarana connects artists, producers, engineers, organizations, and the people who hire them, on one professional platform.</p>
            <div className="hero-actions">
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/redirect')}>Get the app</button>
            </div>
          </div>
          <div className="hero-phones">
            <PhoneFrame className="a" screen="profile" frameWidth={210} />
            <PhoneFrame className="b" screen="hire" frameWidth={236} />
            <div className="phone-glow" />
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="s-bg1">
        <div className="s-wrap" data-reveal style={{ paddingTop: 72, paddingBottom: 72 }}>
          <p className="s-eyebrow">Who it's for</p>
          <h2 className="s-h2">Built for everyone who<br /><em>makes — and hires — music.</em></h2>
        </div>
        <div style={{ padding: '0 52px 80px', maxWidth: 1200, margin: '0 auto' }}>
          <div className="trio" data-reveal="group">
            <div className="trio-card" data-reveal-stagger onClick={() => navigate('/for-artists')}>
              <svg className="tc-icon" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="16" cy="10" r="5" /><path d="M16 15v4m-6 5a6 6 0 0112 0" /><path d="M10 20c-3 1-5 3-5 4h22c0-1-2-3-5-4" />
              </svg>
              <div className="tc-label">For Artists</div>
              <div className="tc-title">Build, discover,<br />get paid.</div>
              <div className="tc-body">Your tracks, credits, and skills in one professional profile. Find paid gigs, collaborate, and get paid safely on every deal.</div>
              <div className="tc-link">Create your profile <ArrowIcon /></div>
            </div>
            <div className="trio-card" data-reveal-stagger onClick={() => navigate('/for-organizations')}>
              <svg className="tc-icon" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="14" width="26" height="14" rx="2" /><rect x="10" y="8" width="12" height="8" rx="2" /><rect x="14" y="3" width="4" height="7" rx="1" /><line x1="16" y1="14" x2="16" y2="28" />
              </svg>
              <div className="tc-label">For Organizations</div>
              <div className="tc-title">Discover, hire,<br />manage.</div>
              <div className="tc-body">Labels, studios, production houses. Search and filter professionals, post gigs, manage your roster, and transact safely with escrow.</div>
              <div className="tc-link">Find talent <ArrowIcon /></div>
            </div>
            <div className="trio-card" data-reveal-stagger onClick={() => navigate('/for-listeners')}>
              <svg className="tc-icon" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12v4a3 3 0 003 3h0a3 3 0 003-3v-4" /><path d="M21 12v4a3 3 0 003 3h0a3 3 0 003-3v-4" /><path d="M5 14A11 11 0 0127 14" /><path d="M8 28h16" />
              </svg>
              <div className="tc-label">For Listeners</div>
              <div className="tc-title">Discover, support,<br />and hire.</div>
              <div className="tc-body">Find emerging artists before everyone else — and book DJs, bands, and solo performers for your events, with safe payment on every booking.</div>
              <div className="tc-link">Start exploring <ArrowIcon /></div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section>
        <div className="problem-wrap" data-reveal>
          <p className="problem-q">The music industry runs on <span>relationships</span> — and nothing connects them.</p>
          <p className="problem-body">India has one of the largest music audiences in the world and a fast-growing independent scene — but no professional home where talent, work, and payment finally come together. Tarana is that home.</p>
          <div className="problem-lines" data-reveal="group">
            <span className="problem-line" data-reveal-stagger>Discovery happens in DMs.</span>
            <span className="problem-line" data-reveal-stagger>Hiring happens by word of mouth.</span>
            <span className="problem-line" data-reveal-stagger>Payments happen on trust.</span>
            <span className="problem-line" data-reveal-stagger>Credits get forgotten.</span>
          </div>
        </div>
      </section>

      {/* For Artists preview */}
      <section className="s-bg1">
        <div className="fsplit" data-reveal>
          <div className="fsplit-text">
            <p className="s-eyebrow">For Artists</p>
            <h2 className="s-h2">Get discovered.<br />Find work.<br /><em>Get paid.</em></h2>
            <p className="s-body">Tarana gives every music professional a real profile, real opportunities, and real protection on every deal.</p>
            <ul className="bullets">
              <li>Build a professional profile with verified credits and your full body of work.</li>
              <li>Find paid gigs and freelance work through the Tarana marketplace.</li>
              <li>Collaborate with producers, session players, and bandmates on real projects.</li>
              <li>Get paid safely, with payments and agreements handled on the platform.</li>
            </ul>
            <div className="cta-actions">
              <button className="btn btn-primary" onClick={() => navigate('/redirect')}>Get the app</button>
              <button className="btn btn-ghost" onClick={() => navigate('/for-artists')}>Explore more</button>
            </div>
          </div>
          <div className="fsplit-visual">
            <PhoneFrame screen="profile" frameWidth={236} />
          </div>
        </div>
      </section>

      {/* For Organizations preview */}
      <section>
        <div className="fsplit rev" data-reveal>
          <div className="fsplit-text">
            <p className="s-eyebrow">For Organizations</p>
            <h2 className="s-h2">Discover talent.<br /><em>Hire with confidence.</em></h2>
            <p className="s-body">Whether you're a label, studio, production house, or music company, Tarana is one place to find, vet, and hire the right people.</p>
            <ul className="bullets">
              <li>Search and filter talent by genre, skill, and location.</li>
              <li>Post gigs and projects to a community of working professionals.</li>
              <li>Manage your roster — track work, agreements, and communication in one place.</li>
              <li>Transact safely with escrow-protected payments.</li>
            </ul>
            <div className="cta-actions">
              <button className="btn btn-primary" onClick={() => navigate('/redirect')}>Get the app</button>
              <button className="btn btn-ghost" onClick={() => navigate('/for-organizations')}>Explore more</button>
            </div>
          </div>
          <div className="fsplit-visual">
            <PhoneFrame screen="hire" frameWidth={236} />
          </div>
        </div>
      </section>

      {/* For Listeners preview */}
      <section className="s-bg1">
        <div className="fsplit" data-reveal>
          <div className="fsplit-text">
            <p className="s-eyebrow">For Listeners</p>
            <h2 className="s-h2">Discover the music.<br />Support the artists.<br /><em>Hire the talent.</em></h2>
            <p className="s-body">Tarana isn't just where you find new music — it's where you book it.</p>
            <ul className="bullets">
              <li>Discover emerging and independent artists across genres and languages.</li>
              <li>Follow and support the artists you love.</li>
              <li>Hire and book talent — DJs, bands, and solo performers — for your events, directly through Tarana.</li>
              <li>Pay safely, with escrow-protected payments on every booking.</li>
            </ul>
            <div className="cta-actions">
              <button className="btn btn-primary" onClick={() => navigate('/redirect')}>Get the app</button>
              <button className="btn btn-ghost" onClick={() => navigate('/for-listeners')}>Explore more</button>
            </div>
          </div>
          <div className="fsplit-visual">
            <PhoneFrame screen="feed" frameWidth={236} screenQuery="hideStories=1" />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section>
        <div className="s-wrap" data-reveal>
          <p className="s-eyebrow">How it works</p>
          <h2 className="s-h2">One platform, the whole<br /><em>music ecosystem.</em></h2>
          <div className="how" data-reveal="group">
            <div className="how-step" data-reveal-stagger>
              <div className="how-num">01</div>
              <div className="how-title">Profile</div>
              <div className="how-body">Build your professional presence with verified credits, tracks, videos, and skills. Your reputation, in one place.</div>
              <div className="how-arrow">→</div>
            </div>
            <div className="how-step" data-reveal-stagger>
              <div className="how-num">02</div>
              <div className="how-title">Discover</div>
              <div className="how-body">Search talent by genre, skill, and location. Find producers, studios, session players, or listeners discovering new artists.</div>
              <div className="how-arrow">→</div>
            </div>
            <div className="how-step" data-reveal-stagger>
              <div className="how-num">03</div>
              <div className="how-title">Collaborate &amp; Hire</div>
              <div className="how-body">Apply to gigs. Post opportunities. Form bands. Build shared profiles. Manage work and communication in one place.</div>
              <div className="how-arrow">→</div>
            </div>
            <div className="how-step" data-reveal-stagger>
              <div className="how-num">04</div>
              <div className="how-title">Get paid</div>
              <div className="how-body">On-platform agreements and escrow-protected payments mean every deal — for artists, organizations, and listeners — is clear and safe.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats 
      <StatCounters /> */}

      {/* Backers */}
      <div className="backers-wrap">
        <div className="backers-inner" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 24 }}>
          <span className="backers-label">Backed by leading institutions and startup programs</span>
          <div className="backers-grid"><BackerTiles /></div>
        </div>
      </div>

      {/* Get started CTA */}
      <section className="s-bg4 cta-strip" data-reveal>
        <p className="s-eyebrow" style={{ justifyContent: 'center' }}>Get started</p>
        <h2 className="s-h2">Start building your<br /><em>music career today.</em></h2>
        <p className="s-body">Download the Tarana app and get started in minutes.</p>
        <div className="app-badges">
          <div className="app-badge" onClick={() => openStore(APP_STORE_URL)}>
            <AppleIcon />
            <div className="app-badge-text"><span className="app-badge-sub">Download on the</span><span className="app-badge-name">App Store</span></div>
          </div>
          <div className="app-badge" onClick={() => openStore(PLAY_STORE_URL)}>
            <GooglePlayIcon className="app-badge-play-icon" />
            <div className="app-badge-text"><span className="app-badge-sub">Get it on</span><span className="app-badge-name">Google Play</span></div>
          </div>
        </div>
      </section>
    </>
  );
}
